import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Building2, CalendarDays, CircleHelp, Handshake, Home, Landmark, Linkedin, ListTodo, Mail, MapPin, Menu, Plus, Settings, Users } from "lucide-react";
import { BouquetIcon } from "../misc/BouquetIcon";
import { openHelp } from "../help/HelpPanel";
import { useZugang } from "../buchhaltung/daten";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (["/events", "/emails", "/locations", "/linkedin", "/buchhaltung", "/settings"].some((p) => matchPath(`${p}/*`, location.pathname))) {
    currentPath = "/mehr";
  } else {
    currentPath = false;
  }


  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 bg-secondary h-14"
      // LDF: Abstand zur Home-Leiste über safe-area (wirkt als App und in Safari gleichermaßen)
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
        height: "calc(3.5rem + env(safe-area-inset-bottom))",
      }}
    >
      {/* LDF: drei Symbole links, „+“ in der Mitte, drei rechts */}
      <div className="mx-auto flex max-w-md items-start justify-between px-1">
        <>
          <NavigationButton
            href="/"
            Icon={Home}
            label="Start"
            isActive={currentPath === "/"}
          />
          <NavigationButton
            href="/contacts"
            Icon={Users}
            label={translate("resources.contacts.name", {
              smart_count: 2,
            })}
            isActive={currentPath === "/contacts"}
          />
          <NavigationButton
            href="/companies"
            Icon={Building2}
            label="Firmen"
            isActive={currentPath === "/companies"}
          />
          <CreateButton />
          <NavigationButton
            href="/deals"
            Icon={Handshake}
            label="Aufträge"
            isActive={currentPath === "/deals"}
          />
          <NavigationButton
            href="/tasks"
            Icon={ListTodo}
            label="Aufgaben"
            isActive={currentPath === "/tasks"}
          />
          <MoreButton isActive={currentPath === "/mehr"} />
        </>
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-col gap-1 h-auto py-2 px-0 rounded-md min-w-0 flex-1",
      isActive ? null : "text-muted-foreground",
    )}
  >
    <Link to={href}>
      <Icon className="size-6" />
      <span className="text-[0.6rem] font-medium truncate max-w-full">{label}</span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="icon"
            className="mx-1 h-14 w-14 shrink-0 rounded-full -mt-3"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-8" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setContactCreateOpen(true);
            }}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setNoteCreateOpen(true);
            }}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setTaskCreateOpen(true);
            }}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

// LDF: „Mehr“ – alle weiteren Bereiche als großes Kachelmenü (Daumen-freundlich)
const MORE_ITEMS: { href: string; label: string; Icon: React.ComponentType<{ className?: string }>; hint: string }[] = [
  { href: "/events", label: "Events", Icon: CalendarDays, hint: "Kalender und Akquise" },
  { href: "/emails", label: "E-Mails", Icon: Mail, hint: "Zu prüfen" },
  { href: "/locations", label: "Locations", Icon: MapPin, hint: "Anfahrt, Parken, Aufbau" },
  { href: "/linkedin", label: "LinkedIn", Icon: Linkedin, hint: "Vernetzen" },
  { href: "/blumengruesse", label: "Blumengrüße", Icon: BouquetIcon, hint: "Auswertung" },
  { href: "/settings", label: "Einstellungen", Icon: Settings, hint: "Profil und CRM" },
];

const MoreButton = ({ isActive }: { isActive: boolean }) => {
  const [open, setOpen] = useState(false);
  const { zugang } = useZugang();
  const items = zugang
    ? [...MORE_ITEMS.slice(0, -1), { href: "/buchhaltung", label: "Buchhaltung", Icon: Landmark, hint: "UStVA und EÜR" }, ...MORE_ITEMS.slice(-1)]
    : MORE_ITEMS;
  return (
    <>
      <Button
        variant="ghost"
        className={cn("flex-col gap-1 h-auto py-2 px-0 rounded-md min-w-0 flex-1", isActive ? null : "text-muted-foreground")}
        onClick={() => setOpen(true)}
        aria-label="Weitere Bereiche"
      >
        <Menu className="size-6" />
        <span className="text-[0.6rem] font-medium">Mehr</span>
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}>
          <SheetHeader>
            <SheetTitle>Weitere Bereiche</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-2 px-4">
            {items.map(({ href, label, Icon, hint }) => (
              <Link
                key={href}
                to={href}
                onClick={() => setOpen(false)}
                className="flex min-h-16 items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 no-underline active:bg-muted"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{label}</span>
                  <span className="block truncate text-xs text-muted-foreground">{hint}</span>
                </span>
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setTimeout(openHelp, 250);
              }}
              className="flex min-h-16 items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 text-left active:bg-muted cursor-pointer"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CircleHelp className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium">Hilfe</span>
                <span className="block truncate text-xs text-muted-foreground">Zur aktuellen Seite</span>
              </span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export const SettingsButton = () => {
  const translate = useTranslate();
  const location = useLocation();
  const isActive = !!matchPath("/settings", location.pathname);

  return (
    <NavigationButton
      href="/settings"
      Icon={Settings}
      label={translate("crm.settings.title")}
      isActive={isActive}
    />
  );
};
